import { type RefObject, useEffect, useState } from 'react';

type WidthTarget = Element | RefObject<Element | null> | null;

/**
 * Returns `true` once the observed element's inline-size is >= `threshold`.
 *
 * Bridges container queries to JS for portaled descendants (e.g. modals)
 * that can't read the bar's container query directly. Pair with a CSS
 * `@container <name> (min-width: <threshold>)` rule using the same value
 * so the bar and the JS-controlled mirror stay in sync.
 *
 * SSR-safe — returns `false` until the first client-side measurement.
 *
 * @param target - Element or ref object to observe.
 * @param threshold - CSS length string. Supports `'<n>px'` and `'<n>em'`
 *   (em resolved against the document root's computed font size).
 */
export function useContainerWidthAbove(
  target: WidthTarget,
  threshold: string,
): boolean {
  const [isAbove, setIsAbove] = useState(false);

  useEffect(() => {
    const element = getElement(target);
    if (!element) return;

    const thresholdPx = parseLengthToPx(threshold);
    if (thresholdPx === null) return;

    const updateWidth = (width: number) => {
      setIsAbove(width >= thresholdPx);
    };

    const measure = () => {
      updateWidth(element.getBoundingClientRect().width);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const width = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      updateWidth(width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [target, threshold]);

  return isAbove;
}

function getElement(target: WidthTarget): Element | null {
  if (!target) return null;
  if ('current' in target) return target.current;
  return target;
}

function parseLengthToPx(value: string): number | null {
  const match = /^(-?\d*\.?\d+)\s*(px|em|rem)?$/.exec(value.trim());
  if (!match) return null;
  const n = parseFloat(match[1]);
  const unit = match[2] ?? 'px';
  if (unit === 'px') return n;
  // em and rem both resolve against the document root in this hook —
  // the threshold is a design constant, not the observed element's local font.
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return n * rootFontSize;
}
